import { setCookie } from '@propertylens/app-commons';
import { captureException } from '@sentry/react';

type UTMParams = Partial<
  Record<
    'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_term' | 'utm_content',
    string | string[] | undefined
  >
>;

export function getUTMParameters(
  query: Record<string, string | string[] | undefined>
) {
  return {
    utm_source: query.utm_source || '',
    utm_medium: query.utm_medium || '',
    utm_campaign: query.utm_campaign || '',
    utm_term: query.utm_term || '',
    utm_content: query.utm_content || '',
  };
}

export function getCookie(name: string): string {
  try {
    if (typeof window === 'undefined') return '';
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    return '';  
  } catch (error) {
    captureException(error);
    return '';
  }
}

export function storeUTMParameters(
  query: Record<string, string | string[] | undefined>
): Record<string, string> {
  const utmParams = getUTMParameters(query);

  Object.keys(utmParams).forEach((key) => {
    if (utmParams[key]) {
      setCookie(key, utmParams[key], 30);
    }
  });

  // Convert any array values to strings for the return type
  const normalizedParams: Record<string, string> = {};
  Object.keys(utmParams).forEach((key) => {
    normalizedParams[key] = Array.isArray(utmParams[key])
      ? utmParams[key][0] || ''
      : utmParams[key] || '';
  });

  return normalizedParams;
}

export function getStoredUTMParameters(): Record<string, string> {
  try {
    const urlParams: UTMParams =
    typeof window !== 'undefined'
      ? getUTMParameters(
          Object.fromEntries(
            new URLSearchParams(window.location.search).entries()
          )
        )
      : {};

    // Normalize URL parameters to strings
    const normalizeValue = (value: string | string[] | undefined): string => {
      if (Array.isArray(value)) {
        return value[0] || '';
      }
      return value || '';
    };

    return {
      utm_source:
        normalizeValue(urlParams.utm_source) || getCookie('utm_source') || '',
      utm_medium:
        normalizeValue(urlParams.utm_medium) || getCookie('utm_medium') || '',
      utm_campaign:
        normalizeValue(urlParams.utm_campaign) || getCookie('utm_campaign') || '',
      utm_term: normalizeValue(urlParams.utm_term) || getCookie('utm_term') || '',
      utm_content:
        normalizeValue(urlParams.utm_content) || getCookie('utm_content') || '',
    };
  } catch (error) {
    captureException(error);
    return {
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_term: '',
      utm_content: '',
    };
  }
  
}

export function getValidUTMParams(
  utmParams?: Record<string, string> | null
): Record<string, string> {
  return utmParams
    ? Object.fromEntries(
        Object.entries(utmParams).filter(
          ([_, value]) => value && value.trim() !== ''
        )
      )
    : {};
}
